/**
 * Blog registry.
 *
 * All posts live as markdown files in `/app/frontend/src/content/blog/*.md`
 * with a small YAML frontmatter block for metadata. Webpack's `asset/source`
 * rule (configured in craco.config.js) imports each file as a raw string, so
 * we parse the frontmatter in-process and expose a fully typed post list to
 * the rest of the app without a CMS, a backend, or a heavy runtime YAML parser.
 *
 * To add a new post: drop another `.md` file into `src/content/blog/` and add
 * a `require(...)` line below. That's it — the index page, detail page, and
 * SEO prerender script all pick it up automatically.
 *
 * Why not `gray-matter` at runtime? `gray-matter` pulls in `js-yaml`, which
 * expects a Node `Buffer` global in the browser. Adding a polyfill for a
 * feature we don't use (arbitrary YAML) would bloat the bundle. Our posts
 * only need a handful of scalar fields plus a `tags: [...]` list, so a
 * ~40-line hand-rolled parser is safer and faster than shipping a full YAML
 * engine to visitors. `gray-matter` still runs at build time inside
 * `scripts/prerender-seo.js` where a real Node runtime is available.
 */

// Explicit registry (instead of `require.context`) keeps the bundle deterministic
// and lets deleting a post be as simple as removing a line.
const rawFiles = [
    require("../content/blog/custom-software-vs-agency-small-business.md"),
];

const FRONTMATTER_RE = /^---\s*\r?\n([\s\S]*?)\r?\n---\s*\r?\n?([\s\S]*)$/;

function stripQuotes(value) {
    const v = value.trim();
    if (
        (v.startsWith('"') && v.endsWith('"')) ||
        (v.startsWith("'") && v.endsWith("'"))
    ) {
        return v.slice(1, -1);
    }
    return v;
}

/**
 * Minimal YAML frontmatter parser. Supports:
 *   key: scalar
 *   key: "quoted scalar"
 *   key:
 *     - item
 *     - item
 *
 * That's the full grammar our blog posts use. Anything more complex
 * would be a signal to move parsing back to a build step.
 */
function parseFrontmatter(block) {
    const lines = block.split(/\r?\n/);
    const data = {};
    let currentKey = null;

    for (const raw of lines) {
        if (!raw.trim()) continue;

        // Continuation of an array under `currentKey`.
        const arrayItem = raw.match(/^\s+-\s+(.*)$/);
        if (arrayItem && currentKey) {
            if (!Array.isArray(data[currentKey])) data[currentKey] = [];
            data[currentKey].push(stripQuotes(arrayItem[1]));
            continue;
        }

        const kv = raw.match(/^([A-Za-z0-9_-]+)\s*:\s*(.*)$/);
        if (!kv) continue;

        const key = kv[1];
        const value = kv[2];
        currentKey = key;

        if (value.trim() === "") {
            // Value on following lines (expecting an array).
            data[key] = [];
        } else {
            data[key] = stripQuotes(value);
            currentKey = null;
        }
    }

    return data;
}

function parsePost(raw) {
    const match = raw.match(FRONTMATTER_RE);
    if (!match) {
        throw new Error("Blog post is missing frontmatter block.");
    }
    const data = parseFrontmatter(match[1]);
    const content = (match[2] || "").trim();

    if (!data.slug) {
        throw new Error("Blog post is missing `slug` in frontmatter.");
    }

    const wordCount = content.split(/\s+/).filter(Boolean).length;
    const readingMinutes = Math.max(1, Math.round(wordCount / 220));

    // Normalize date to YYYY-MM-DD.
    let date = "";
    if (data.date) {
        const d = new Date(data.date);
        date = Number.isNaN(d.getTime())
            ? String(data.date)
            : d.toISOString().slice(0, 10);
    }

    return {
        slug: data.slug,
        lang: data.lang || "en",
        title: data.title || "",
        description: data.description || "",
        date,
        author: data.author || "Guillermo Figueroa Mesa",
        tags: Array.isArray(data.tags) ? data.tags : [],
        ogImage: data.ogImage || "/og/default.jpg",
        content,
        readingMinutes,
    };
}

const allPosts = rawFiles.map(parsePost);

/** Newest-first (by frontmatter date). Filters by language when provided. */
export function getPosts({ lang } = {}) {
    const filtered = lang ? allPosts.filter((p) => p.lang === lang) : allPosts;
    return [...filtered].sort((a, b) => b.date.localeCompare(a.date));
}

/** Look up a single post by its slug. Language-agnostic. */
export function getPostBySlug(slug) {
    return allPosts.find((p) => p.slug === slug) || null;
}

/** Distinct list of tags across all posts. Used by the blog index for filtering. */
export function getAllTags({ lang } = {}) {
    const posts = getPosts({ lang });
    const set = new Set();
    posts.forEach((p) => p.tags.forEach((t) => set.add(t)));
    return Array.from(set).sort();
}
